import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Search, MapPin, Accessibility, Heart, ArrowLeft, 
  ExternalLink, Info, HelpCircle, Flower2, Stethoscope,
  Eye, Ear, Brain, Activity
} from "lucide-react";
import BottomNav from "@/components/BottomNav";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SenCorner = () => {
  const { user } = useAuth();
  const [resources, setResources] = useState(null);
  const [changingPlaces, setChangingPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchCity, setSearchCity] = useState("");
  const [activeTab, setActiveTab] = useState("resources");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [resourcesRes, placesRes] = await Promise.all([
        axios.get(`${API}/sen/resources`),
        axios.get(`${API}/sen/changing-places`)
      ]);
      setResources(resourcesRes.data);
      setChangingPlaces(placesRes.data.facilities || []);
    } catch (error) {
      console.error("Error fetching SEN data:", error);
    } finally {
      setLoading(false);
    }
  };

  const searchChangingPlaces = async () => {
    if (!searchCity.trim()) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sen/changing-places`, {
        params: { city: searchCity }
      });
      setChangingPlaces(res.data.facilities || []);
    } catch (error) {
      console.error("Error searching:", error);
    } finally {
      setLoading(false);
    }
  };

  const getIconForCode = (code) => {
    const icons = {
      wheelchair_access: Accessibility,
      changing_places: Activity,
      sensory_friendly: Ear,
      quiet_space: Brain,
      autism_friendly: Heart,
      visual_aids: Eye,
      hearing_loop: Ear,
      staff_trained: HelpCircle
    };
    return icons[code] || Info;
  };

  return (
    <div className="min-h-screen bg-[#FFF9F5] pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-500 to-purple-500 text-white p-6 pt-8">
        <div className="flex items-center gap-3 mb-2">
          <Link to="/dashboard" className="p-2 -ml-2 hover:bg-white/10 rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>
              SEN Corner
            </h1>
            <p className="text-white/80 text-sm">Supporting families with additional needs</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 bg-white sticky top-0 z-10">
        <button
          onClick={() => setActiveTab("resources")}
          className={`flex-1 py-3 text-sm font-medium transition-colors ${
            activeTab === "resources"
              ? 'text-teal-600 border-b-2 border-teal-500'
              : 'text-slate-500'
          }`}
        >
          Resources
        </button>
        <button
          onClick={() => setActiveTab("changing-places")}
          className={`flex-1 py-3 text-sm font-medium transition-colors ${
            activeTab === "changing-places"
              ? 'text-teal-600 border-b-2 border-teal-500'
              : 'text-slate-500'
          }`}
        >
          Changing Places
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : activeTab === "resources" ? (
        <div className="p-4 space-y-6">
          {/* Sunflower Lanyard Info */}
          {resources?.sunflower_lanyard && (
            <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-2xl p-5 border border-amber-100">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                  <Flower2 className="w-6 h-6 text-yellow-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-800">Sunflower Lanyard Scheme</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    {resources.sunflower_lanyard.description}
                  </p>
                  <a
                    href={resources.sunflower_lanyard.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm text-amber-600 hover:text-amber-700 font-medium mt-2"
                  >
                    Learn more <ExternalLink className="w-3 h-3 ml-1" />
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Accessibility Features */}
          <div>
            <h2 className="text-lg font-semibold text-slate-800 mb-3">Accessibility Features</h2>
            <p className="text-sm text-slate-600 mb-4">
              Look for these features when choosing venues for your family
            </p>
            <div className="grid grid-cols-2 gap-3">
              {resources?.accessibility_features?.map(feature => {
                const Icon = getIconForCode(feature.code);
                return (
                  <div key={feature.code} className="bg-white rounded-xl p-4 border border-slate-100">
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center mb-2">
                      <Icon className="w-5 h-5 text-teal-600" />
                    </div>
                    <p className="font-medium text-slate-800 text-sm">{feature.label}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Child Support Options */}
          <div>
            <h2 className="text-lg font-semibold text-slate-800 mb-3">Support Categories</h2>
            <p className="text-sm text-slate-600 mb-4">
              You can add these to your child's profile for better matching
            </p>
            <div className="flex flex-wrap gap-2">
              {resources?.child_support_options?.map(option => (
                <span key={option.code} className="px-3 py-2 bg-purple-100 text-purple-700 rounded-full text-sm">
                  {option.label}
                </span>
              ))}
            </div>
          </div>

          {/* Medical Needs */}
          <div>
            <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <Stethoscope className="w-5 h-5 text-pink-500" />
              Medical Needs Support
            </h2>
            <div className="flex flex-wrap gap-2">
              {resources?.medical_needs?.map(need => (
                <span key={need.code} className="px-3 py-2 bg-pink-100 text-pink-700 rounded-full text-sm">
                  {need.label}
                </span>
              ))}
            </div>
          </div>

          {/* Mobility Options */}
          <div>
            <h2 className="text-lg font-semibold text-slate-800 mb-3">Mobility Support</h2>
            <div className="flex flex-wrap gap-2">
              {resources?.mobility_options?.map(option => (
                <span key={option.code} className="px-3 py-2 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-1">
                  <Accessibility className="w-4 h-4" />
                  {option.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="p-4">
          {/* Changing Places Info */}
          <div className="bg-teal-50 rounded-2xl p-4 border border-teal-100 mb-4">
            <h3 className="font-semibold text-teal-800 flex items-center gap-2">
              <Accessibility className="w-5 h-5" />
              What are Changing Places?
            </h3>
            <p className="text-sm text-teal-700 mt-2">
              Changing Places toilets are fully accessible facilities with hoists, adult-sized changing benches, and space for carers. Essential for people with profound disabilities.
            </p>
            <a
              href="https://www.changing-places.org"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-sm text-teal-600 hover:text-teal-700 font-medium mt-2"
            >
              Visit Changing Places UK <ExternalLink className="w-3 h-3 ml-1" />
            </a>
          </div>

          {/* Search */}
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search by city or postcode..."
                value={searchCity}
                onChange={(e) => setSearchCity(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchChangingPlaces()}
                className="pl-10"
                data-testid="changing-places-search"
              />
            </div>
            <Button onClick={searchChangingPlaces} className="bg-teal-500 hover:bg-teal-600">
              <Search className="w-4 h-4" />
            </Button>
          </div>

          {/* Results */}
          {changingPlaces.length === 0 ? (
            <div className="text-center py-8">
              <Accessibility className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600">Search for Changing Places in your area</p>
              <p className="text-slate-400 text-sm mt-1">
                Enter a city or postcode to find facilities near you
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">{changingPlaces.length} facilities found</p>
              {changingPlaces.map((place, i) => (
                <div key={i} className="bg-white rounded-xl p-4 border border-slate-100">
                  <h4 className="font-medium text-slate-800">{place.name}</h4>
                  <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                    <MapPin className="w-3 h-3" />
                    {place.address}
                  </p>
                  {place.postcode && (
                    <p className="text-xs text-slate-400 mt-1">{place.postcode}</p>
                  )}
                  {place.facilities && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {place.facilities.map((f, j) => (
                        <span key={j} className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded">
                          {f}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <p className="text-xs text-center text-slate-400 mt-6">
            Data sourced from Changing Places UK. For the most up-to-date information, visit{" "}
            <a href="https://www.changing-places.org/find" target="_blank" rel="noopener noreferrer" className="text-teal-600 underline">
              changing-places.org
            </a>
          </p>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default SenCorner;
